import { i18n } from "@/plugins/vuetify";
import {firestore} from "firebase/app";
import {DateTime} from "luxon";
import {getMinutesSince, areDatesOnSameDay} from "./DateHelper";

export function formatDebug(
    value: Date | firestore.Timestamp | number | DateTime | null | undefined
): string {
    const format = "dd.MM.yy HH:mm";
    if (!value) {
        return "???";
    } else if (value instanceof Date) {
        return formatDebug(DateTime.fromJSDate(value));
    } else if (value instanceof firestore.Timestamp) {
        return formatDebug(DateTime.fromJSDate(value.toDate()));
    } else if (value instanceof DateTime) {
        return value.setLocale(i18n.locale).toFormat(format);
    } else {
        return formatDebug(DateTime.fromJSDate(new Date(value)));
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getDate(value: any): Date {
    if (value.toDate) {
        return value.toDate();
    } else {
        return value as Date;
    }
}

export function toDatePickerFormat(date: Date): string {
    return DateTime.fromJSDate(date).toFormat("yyyy-MM-dd");
}

export function timestampToDate(timestamp: firestore.Timestamp): Date | null {
    return timestamp ? new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000) : null;
}

export function isSameYearAsCurrent(date: Date) {
    return date.getFullYear() === new Date().getFullYear();
}

export function formatDateHoursMinutes(
    date: Date,
    excludeDateIfDateIsToday?: boolean
): string {
    const now = new Date();

    if (excludeDateIfDateIsToday && areDatesOnSameDay(now, date)) {
        return DateTime.fromJSDate(date).toFormat("HH:mm");
    }

    const isSameYearAsToday = isSameYearAsCurrent(date);

    return DateTime.fromJSDate(date).toFormat(
        isSameYearAsToday ? "dd.MM. HH:mm" : "dd.MM.yy HH:mm"
    );
}

export function formatDate(date: Date, format: string) {
  return DateTime.fromJSDate(date).toFormat(format);
}

export function formatTimestampHoursMinutes(
    timestamp: firestore.Timestamp
): string {
    return formatDateHoursMinutes(timestamp.toDate());
}

export function formatDateLong(timestamp: firestore.Timestamp): string {
    const adjustedDate = timestamp.toDate();
    const isCurrentYear =
      new Date().getFullYear() === adjustedDate.getFullYear();

    return (
      isCurrentYear ?
          DateTime.fromJSDate(adjustedDate).toFormat("dd.MM.") :
          DateTime.fromJSDate(adjustedDate).toFormat("dd.MM.yy")
    );
}

export function formatDateCompact(timestamp: firestore.Timestamp) {
  return DateTime.fromJSDate(timestamp.toDate()).toFormat("yyMMddHHmm");
}

export function formatDateForReportsFolderName(timestamp: firestore.Timestamp) {
  return DateTime.fromJSDate(timestamp.toDate()).toFormat("dd-MM-HH-mm");
}

export function formatDateShort(
  value: firestore.Timestamp | Date,
  hideYear?: boolean,
  convertToDate?: boolean,
  skipCurrentYearCheck?: boolean
): string {
  if (!value) {
    return "";
  }

  let date = getDate(value);

  if (convertToDate) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dateToBeChecked = date as any;

    if (!dateToBeChecked.getFullYear) {
      date = timestampToDate(dateToBeChecked as firestore.Timestamp) ?? date;
    }
  }

  return DateTime.fromJSDate(date).toFormat(
    hideYear || (!skipCurrentYearCheck && isSameYearAsCurrent(date))
      ? "dd.MM."
      : "dd.MM.yy"
  );
}

export function dateFromIsoString(isoString: string) {
    const tokens = isoString.split("-");

    return DateTime.local(
        parseInt(tokens[0]),
        parseInt(tokens[1]),
        parseInt(tokens[2])
    ).toJSDate();
}

export function dateTimeFromIsoString(isoString: string) {
  const dateTime = DateTime.fromISO(isoString);

  return dateTime.toJSDate();
}

export function fromIsoString(isoString: string) {
    return firestore.Timestamp.fromDate(dateFromIsoString(isoString));
}

export function fromTimeString(timestamp: string) {
    return firestore.Timestamp.fromDate(new Date(timestamp));
}

export function toIsoString(timestamp: firestore.Timestamp) {
    return timestamp
        .toDate()
        .toISOString()
        .substring(0, 10);
}

export function formatTime(
    value: firestore.Timestamp | Date,
    checkHoursAndMinutes?: boolean
): string {
    const adjustedDate = getDate(value);

    if (
        checkHoursAndMinutes &&
        adjustedDate.getHours() == 0 &&
        adjustedDate.getMinutes() == 0
    ) {
        return "";
    }

    return DateTime.fromJSDate(adjustedDate).toFormat("HH:mm");
}

export function formatFullJsDate(date: Date) {
    if (date.getHours() === 0 && date.getMinutes() === 0) {
        return DateTime.fromJSDate(date).toFormat("dd.MM.yy");
    } else {
        return DateTime.fromJSDate(date).toFormat("dd.MM.yy HH:mm");
    }
}

export function formatShortJsDate(date: Date) {
    if (date.getHours() === 0 && date.getMinutes() === 0) {
        return DateTime.fromJSDate(date).toFormat("dd.MM.");
    } else {
        return DateTime.fromJSDate(date).toFormat("dd.MM. HH:mm");
    }
}

export function formatDateAndMonth(date: Date) {
    const isCurrentYear =
        new Date().getFullYear() === date.getFullYear();

    return (
        isCurrentYear ?
            DateTime.fromJSDate(date).toFormat("dd.MM.") :
            DateTime.fromJSDate(date).toFormat("dd.MM.yy")
    );
}

export function formatJsDate(date: Date, showTime = true) {
    if (showTime) {
        return DateTime.fromJSDate(date).toFormat("dd.MM. HH:mm");
    } else {
        return DateTime.fromJSDate(date).toFormat("dd.MM.");
    }
}

export function formatDateWithWeekday(
    value: Date | firestore.Timestamp,
    options?: {
        showTime?: boolean;
        showYearIfNotCurrentYear?: boolean;
    }
) {
    const date = getDate(value);
    const {showTime, showYearIfNotCurrentYear} = options ?? {};

    const dateTime = DateTime.fromJSDate(date).setLocale(i18n.locale);
    //Hack luxon does not support formatting to two letter weekdays
    const twoLetterWeekday = dateTime.toFormat("EEE").slice(0, 2) + " ";
    const dateMonthYearFormat =
        showYearIfNotCurrentYear && new Date().getFullYear() !== date.getFullYear()
            ? "dd.MM.yy"
            : "dd.MM.";

    if (showTime) {
        return twoLetterWeekday + dateTime.toFormat(`${dateMonthYearFormat} HH:mm`);
    } else {
        return twoLetterWeekday + dateTime.toFormat(dateMonthYearFormat);
    }
}

export function getDateForFile(date: Date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}${month}${year}-${hours}${minutes}${seconds}`;
}

export function formatJsTime(date: Date) {
    return DateTime.fromJSDate(date).toFormat("HH:mm");
}

export function formatDateAndTime(
    date: firestore.Timestamp,
    time?: firestore.Timestamp
): string {
    let dateFromString: Date | null = null;
    let timeFromString: Date | null = null;

    if (typeof (date) === "string") {
        dateFromString = new Date(date);
    }

    if (!time) {
        return formatDateShort(dateFromString ?? date);
    }

    if (typeof (time) === "string") {
        timeFromString = new Date(time);
    }

    const jsDate = dateFromString ?? date.toDate();
    const jsTime = timeFromString ?? time.toDate();

    if (jsTime.getHours() === 0 && jsTime.getMinutes() === 0) {
        return formatDateShort(dateFromString ?? date);
    }

    jsDate.setHours(jsTime.getHours(), jsTime.getMinutes(), jsTime.getSeconds());

    return formatJsDate(jsDate);
}

export function formatDurationLong(
    vue: Vue,
    durationInMin: number,
    openEnded: boolean,
    showNow?: boolean
) {
    if (showNow && durationInMin <= 0) {
        return vue.$t("common.duration.now");
    }

    if (durationInMin < 60) {
        return vue.$tc("common.duration.formatMinutes", durationInMin).toString();
    }

    const durationInHours = Math.floor(durationInMin / 60);

    if (durationInHours < 24) {
        return vue.$tc("common.duration.formatHours", durationInHours).toString();
    }

    const durationInDays = Math.floor(durationInMin / (60 * 24));

    if (durationInDays === 1) {
        return vue.$tc("common.duration.formatDays", durationInDays).toString();
    } else {
        if (openEnded) {
            return vue
                .$tc("common.duration.formatDaysOpenEnded", durationInDays)
                .toString();
        } else {
            return vue.$tc("common.duration.formatDays", durationInDays).toString();
        }
    }
}

export function formatDurationSince(vue: Vue, timestamp: firestore.Timestamp) {
    return formatDurationLong(vue, getMinutesSince(timestamp), true);
}

export function formatDurationShort(durationInMin: number) {
    const mins = durationInMin % 60;
    const hours = Math.floor(durationInMin / 60) % 24;
    const days = Math.floor(durationInMin / (24 * 60));

    if (days === 0 && hours === 0) {
        return `${mins}m`;
    }

    if (days === 0) {
        return `${hours}h ${mins}m`;
    }

    return `${days}d ${hours}h`;
}

export function formatDayOfTheWeek(vue: Vue, dayOfTheWeek: number) {
    let result = "monday";

    if (dayOfTheWeek == 1) {
        result = "tuesday";
    } else if (dayOfTheWeek == 2) {
        result = "wednesday";
    } else if (dayOfTheWeek == 3) {
        result = "thursday";
    } else if (dayOfTheWeek == 4) {
        result = "friday";
    } else if (dayOfTheWeek == 5) {
        result = "saturday";
    } else if (dayOfTheWeek == 6) {
        result = "sunday";
    }

    return vue.$t(`common.dayOfTheWeek.${result}`);
}

export function formatDayOfTheWeekForDate(vue: Vue, date: Date) {
    const dayOfTheWeek = (date.getDay() + 6) % 7;
    return formatDayOfTheWeek(vue, dayOfTheWeek);
}
